import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Calculator, TrendingUp, Phone, Globe, Target, DollarSign, Wallet, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";

/**
 * Public ProspectMax ROI Calculator.
 * Mirrors the Excel calculator embedded on slide 14. Prospects reach this page
 * via the QR code on the branded deck, fill in the 5 green-box inputs, and see
 * the resulting monthly opportunity, revenue, gross profit, and ROI % live.
 * No auth required — public route.
 */
export default function ROICalculatorPage() {
  const [params] = useSearchParams();
  const agencyId = params.get("agency") || null;
  const repId = params.get("rep") || null;

  // Sensible default scenario (a medium-sized service business)
  const [missedCalls, setMissedCalls] = useState(15);
  const [missedWeb, setMissedWeb] = useState(10);
  const [convRate, setConvRate] = useState(30);
  const [ltv, setLtv] = useState(5000);
  const [cost, setCost] = useState(695);

  // "Email me this projection" form state
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [prospectName, setProspectName] = useState("");
  const [prospectCompany, setProspectCompany] = useState("");
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState(false);

  // Fetch agency logo (if a known agency_id is in the QR URL) so the page feels co-branded
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    if (!agencyId) return;
    const url = `/api/agencies/${agencyId}/logo`;
    // We only display it if it exists — let an <img onError> hide it
    setLogoUrl(url);
  }, [agencyId]);

  // Math is purely client-side — same formulas the backend endpoint /api/roi/prospectmax exposes
  const roi = useMemo(() => {
    const missedMonthly = (Number(missedCalls || 0) + Number(missedWeb || 0)) * 30;
    const r = Math.max(0, Math.min(Number(convRate || 0), 100)) / 100;
    const newCustomers = missedMonthly * r;
    const revenue = newCustomers * Number(ltv || 0);
    const grossProfit = revenue - Number(cost || 0);
    const roiPct = Number(cost || 0) > 0 ? Math.round((grossProfit / Number(cost)) * 100) : 0;
    return {
      missedMonthly: Math.round(missedMonthly),
      newCustomers: Number(newCustomers.toFixed(1)),
      revenue: Math.round(revenue),
      grossProfit: Math.round(grossProfit),
      roiPct,
    };
  }, [missedCalls, missedWeb, convRate, ltv, cost]);

  const fmt$ = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  const submitRoiEmail = async () => {
    if (!emailTo.trim() || !/.+@.+\..+/.test(emailTo.trim())) {
      toast.error("Please enter a valid email");
      return;
    }
    setEmailing(true);
    try {
      const r = await axios.post(`${API_BASE}/api/roi/prospectmax/email`, {
        to_email: emailTo.trim(),
        prospect_name: prospectName.trim() || null,
        prospect_company: prospectCompany.trim() || null,
        agency_id: agencyId,
        rep_id: repId,
        inputs: {
          missed_calls_daily: Number(missedCalls || 0),
          missed_website_leads_daily: Number(missedWeb || 0),
          conversion_rate_pct: Number(convRate || 0),
          lifetime_value: Number(ltv || 0),
          monthly_program_cost: Number(cost || 0),
        },
      });
      if (r.data?.email_sent === false) {
        const status = r.data?.email_status;
        const msg = status === "test_mode"
          ? "This environment is in TEST mode — the email was NOT delivered. Ask a Super Admin to remove TEST_MODE from the deployment."
          : "Server accepted the request but did not deliver the email (Resend not configured).";
        toast.warning(msg, { duration: 12000 });
      } else {
        setEmailed(true);
        toast.success(`Sent! Check ${emailTo.trim()}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setEmailing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F2747] via-[#162B49] to-[#081A33]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Co-branded header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 text-[#FCA5A5] text-xs font-bold tracking-widest uppercase">
              <Calculator className="h-4 w-4" /> ProspectMax ROI Calculator
            </div>
            <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white mt-2">
              See your potential return on lead capture
            </h1>
            <p className="text-zinc-300 mt-2 text-sm max-w-2xl">
              Adjust the five inputs to match your business. Your results update instantly.
              Each missed call or web lead you recover compounds into real revenue.
            </p>
          </div>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Partner logo"
              className="h-16 w-auto object-contain bg-white/95 rounded-lg px-3 py-2 shrink-0"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="roi-agency-logo"
            />
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* INPUTS — match the green boxes from the Excel */}
          <Card className="border-2 border-emerald-400/40 bg-white/95 backdrop-blur" data-testid="roi-inputs-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500" />
                Your inputs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <NumberRow icon={<Phone className="h-4 w-4 text-rose-600" />}
                          label="Missed calls per day"
                          help="Calls that ring through to voicemail or go unanswered"
                          value={missedCalls} onChange={setMissedCalls} testid="roi-missed-calls" />
              <NumberRow icon={<Globe className="h-4 w-4 text-rose-600" />}
                          label="Missed website leads per day"
                          help="Form fills, chat starts, after-hours visits, etc."
                          value={missedWeb} onChange={setMissedWeb} testid="roi-missed-web" />
              <NumberRow icon={<Target className="h-4 w-4 text-rose-600" />}
                          label="Your close rate (%)"
                          help="Of leads you actually contact, what % become customers"
                          value={convRate} onChange={setConvRate} suffix="%" max={100}
                          testid="roi-conv-rate" />
              <NumberRow icon={<DollarSign className="h-4 w-4 text-rose-600" />}
                          label="Customer lifetime value ($)"
                          help="Average total revenue per customer over their lifetime"
                          value={ltv} onChange={setLtv} prefix="$"
                          testid="roi-ltv" />
              <NumberRow icon={<Wallet className="h-4 w-4 text-rose-600" />}
                          label="Monthly program cost ($)"
                          help="Total monthly cost of the ProspectMax program you select"
                          value={cost} onChange={setCost} prefix="$"
                          testid="roi-cost" />
            </CardContent>
          </Card>

          {/* RESULTS */}
          <Card className="border-2 border-[#FCA5A5]/40 bg-[#0F2747] text-white" data-testid="roi-results-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-white">
                <TrendingUp className="h-5 w-5 text-[#FCA5A5]" />
                Your projected ROI
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ResultRow label="Total missed opportunities / month" value={roi.missedMonthly.toLocaleString()} />
              <ResultRow label="New customers recovered / month" value={roi.newCustomers.toLocaleString()} />
              <ResultRow label="New customer revenue / month" value={fmt$(roi.revenue)} highlight />
              <div className="h-px bg-white/15 my-2" />
              <ResultRow label="Less monthly program cost" value={`− ${fmt$(Number(cost || 0))}`} muted />
              <ResultRow label="Monthly gross profit" value={fmt$(roi.grossProfit)} highlight />
              <div className="bg-[#FCA5A5]/15 border border-[#FCA5A5]/40 rounded-lg p-4 mt-2">
                <div className="text-xs text-[#FCA5A5] uppercase tracking-wider font-semibold">Monthly ROI</div>
                <div className="text-5xl font-bold mt-1 text-white" data-testid="roi-final-pct">
                  {Number.isFinite(roi.roiPct) ? roi.roiPct.toLocaleString() : "—"}%
                </div>
                <div className="text-xs text-zinc-300 mt-1">Return for every dollar invested in the program</div>
              </div>
              <p className="text-[11px] text-zinc-400 leading-relaxed pt-1">
                Estimates only. Real-world results depend on your offer, response speed, and how aggressively
                your team works the captured leads. ProspectMax response-time alerts are designed to maximize all of these.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Email me my ROI projection */}
        <Card className="border-2 border-emerald-400/40 bg-white/95 mt-6" data-testid="roi-email-card">
          <CardContent className="py-6">
            {emailed ? (
              <div className="flex items-center gap-3 text-emerald-700" data-testid="roi-emailed-confirm">
                <CheckCircle2 className="h-6 w-6 shrink-0" />
                <div>
                  <div className="font-semibold">Sent! Check <span className="text-zinc-900">{emailTo}</span></div>
                  <div className="text-xs text-muted-foreground">Your ROI projection PDF is in your inbox. Reply to that email to start the conversation.</div>
                </div>
              </div>
            ) : !emailFormOpen ? (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Mail className="h-6 w-6 text-emerald-600 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-sm">Want this projection in your inbox?</h3>
                    <p className="text-xs text-muted-foreground">We&apos;ll send you a co-branded PDF you can share with your team.</p>
                  </div>
                </div>
                <Button onClick={() => setEmailFormOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="roi-email-open-btn">
                  Email Me My ROI
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Mail className="h-5 w-5 text-emerald-600" /> Email me this projection
                </h3>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Your email *</Label>
                    <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="you@yourcompany.com" data-testid="roi-email-to-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Your name</Label>
                    <Input value={prospectName} onChange={(e) => setProspectName(e.target.value)} placeholder="First Last" data-testid="roi-email-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Company</Label>
                    <Input value={prospectCompany} onChange={(e) => setProspectCompany(e.target.value)} placeholder="Acme Inc." data-testid="roi-email-company-input" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" onClick={() => setEmailFormOpen(false)}>Cancel</Button>
                  <Button onClick={submitRoiEmail} disabled={emailing || !emailTo.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="roi-email-send-btn">
                    {emailing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : "Send Me My ROI"}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  We&apos;ll only use your email to send this projection and follow up about your ROI. No marketing spam.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-zinc-500 text-xs mt-8">
          Powered by Ready Prospect · ProspectMax Lead Management
        </p>
      </div>
    </div>
  );
}

function NumberRow({ icon, label, help, value, onChange, prefix, suffix, max, testid }) {
  return (
    <div>
      <Label className="flex items-center gap-2 text-sm font-medium">
        {icon} {label}
      </Label>
      <div className="relative mt-1">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">{prefix}</span>
        )}
        <Input
          type="number"
          min="0"
          max={max}
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
          className={`${prefix ? "pl-7" : ""} ${suffix ? "pr-8" : ""} bg-emerald-50/60 border-emerald-200 focus:border-emerald-400 text-base font-semibold`}
          data-testid={testid}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">{suffix}</span>
        )}
      </div>
      {help && <p className="text-[11px] text-muted-foreground mt-1">{help}</p>}
    </div>
  );
}

function ResultRow({ label, value, highlight, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${muted ? "text-zinc-400" : "text-zinc-200"}`}>{label}</span>
      <span className={`text-base font-semibold ${highlight ? "text-[#FCA5A5]" : muted ? "text-zinc-400" : "text-white"}`}>
        {value}
      </span>
    </div>
  );
}
